import type { GetStaticPaths, GetStaticProps } from "next";
import Link from "next/link";
import { FooterSite, HeaderSite, SeoHead } from "@acme/feature/landing";
import {
    capabilityLabel, compareFindings, compareSlug, formatContext, formatMonth, formatPrice,
    getComparePairs, getModelPage, parseCompareSlug, type ModelPageData,
} from "../../lib/model-pages";

type Props = { a: ModelPageData; b: ModelPageData; findings: string[]; related: { slug: string; label: string }[] };

export default function ComparePage({ a, b, findings, related }: Props) {
    // Opens the app in a new chat on `a` with compare mode against `b`
    // (handled by useModelDeepLink).
    const appCompareHref = `/chat?model=${encodeURIComponent(a.id)}&compare=${encodeURIComponent(b.id)}`;
    const rows: { label: string; a: string; b: string }[] = [
        { label: 'Provider', a: a.providerName, b: b.providerName },
        { label: 'Released', a: formatMonth(a.releaseDate), b: formatMonth(b.releaseDate) },
        { label: 'Context window', a: `${formatContext(a.contextWindow)} tokens`, b: `${formatContext(b.contextWindow)} tokens` },
        { label: 'Max output', a: `${formatContext(a.maxOutputTokens)} tokens`, b: `${formatContext(b.maxOutputTokens)} tokens` },
        { label: 'Reasoning model', a: a.reasoning ? 'Yes' : 'No', b: b.reasoning ? 'Yes' : 'No' },
        { label: 'Price per 1M tokens (in / out)', a: formatPrice(a.pricing), b: formatPrice(b.pricing) },
        { label: 'Capabilities', a: a.capabilities.map(capabilityLabel).filter(Boolean).join(', '), b: b.capabilities.map(capabilityLabel).filter(Boolean).join(', ') },
    ];
    const faq = [
        { question: `Which has a bigger context window, ${a.name} or ${b.name}?`, answer: findings[0] ?? '' },
        { question: `Can I use ${a.name} and ${b.name} in the same app?`, answer: `Yes. DapperGPT lets you add a ${a.providerName} key and a ${b.providerName} key and switch between ${a.name} and ${b.name} in any chat.` },
    ];

    return (
        <>
            <SeoHead
                path={`/compare/${compareSlug(a.id, b.id)}`}
                title={`${a.name} vs ${b.name}: Context, Output, Price & Release | DapperGPT`}
                description={`${a.name} (${a.providerName}) vs ${b.name} (${b.providerName}) compared on context window, max output, release date, price and capabilities. Try both with your own API keys.`}
                faq={faq}
            />
            <div className="bg-white dark:bg-gray-900 min-h-screen flex flex-col text-gray-900 dark:text-gray-100">
                <HeaderSite containerClassName="!max-w-7xl px-6" />
                <main className="flex-grow container mx-auto max-w-4xl px-4 py-14">
                    <nav className="text-sm text-gray-500" aria-label="Breadcrumb">
                        <Link href="/models" className="hover:underline">Models</Link> <span aria-hidden>/</span> Compare
                    </nav>
                    <h1 className="mt-3 text-4xl md:text-5xl font-bold tracking-tight">{a.name} vs {b.name}</h1>
                    <p className="mt-3 text-lg text-gray-600 dark:text-gray-400">
                        How {a.providerName}&apos;s {a.name} and {b.providerName}&apos;s {b.name} differ on paper, from the specs each provider publishes.
                    </p>
                    <div className="mt-6 flex flex-wrap items-center gap-3">
                        <Link href={appCompareHref} rel="nofollow" className="rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white hover:bg-blue-700">
                            Compare them on your prompt
                        </Link>
                        <span className="text-sm text-gray-500 dark:text-gray-400">Both answer side by side, free with your own API keys.</span>
                    </div>

                    <section className="mt-10">
                        <h2 className="text-xl font-semibold">Key differences</h2>
                        <ul className="mt-4 space-y-3 list-disc pl-5 text-gray-700 dark:text-gray-300">
                            {findings.map(f => <li key={f}>{f}</li>)}
                        </ul>
                    </section>

                    <section className="mt-10 overflow-x-auto">
                        <h2 className="text-xl font-semibold">Side by side</h2>
                        <table className="mt-4 w-full min-w-[520px] text-sm">
                            <thead>
                                <tr className="text-left">
                                    <th className="py-2 w-1/3"><span className="sr-only">Spec</span></th>
                                    <th className="py-2 font-semibold"><Link href={`/models/${a.id}`} className="text-blue-700 dark:text-blue-400 hover:underline">{a.name}</Link></th>
                                    <th className="py-2 font-semibold"><Link href={`/models/${b.id}`} className="text-blue-700 dark:text-blue-400 hover:underline">{b.name}</Link></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(r => (
                                    <tr key={r.label} className="border-t border-gray-100 dark:border-gray-800 align-top">
                                        <th scope="row" className="py-3 pr-4 text-left font-medium text-gray-500 dark:text-gray-400">{r.label}</th>
                                        <td className="py-3 pr-4 tabular-nums">{r.a}</td>
                                        <td className="py-3 tabular-nums">{r.b}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p className="mt-3 text-xs text-gray-500">Specs as published by each provider. Benchmarks and output quality aren&apos;t covered here; the best test is your own prompts.</p>
                    </section>

                    <div className="mt-12 rounded-2xl bg-gray-900 dark:bg-gray-800 text-white p-8">
                        <p className="text-2xl font-semibold">Specs only go so far. Ask both.</p>
                        <p className="mt-2 text-gray-300">Send one message and get {a.name} and {b.name}&apos;s answers side by side, with response time, tokens and cost for each. You&apos;ll need {a.providerName === b.providerName ? `a ${a.providerName} key` : `${a.providerName} and ${b.providerName} keys`}.</p>
                        <Link href={appCompareHref} rel="nofollow" className="mt-5 inline-block rounded-lg bg-white text-gray-900 px-6 py-3 font-semibold hover:bg-gray-100">Open {a.name} vs {b.name}</Link>
                    </div>

                    {related.length > 0 && (
                        <section className="mt-12">
                            <h2 className="text-xl font-semibold">Other comparisons</h2>
                            <ul className="mt-4 grid sm:grid-cols-2 gap-2 list-none p-0">
                                {related.map(c => (
                                    <li key={c.slug} className="list-none"><Link href={`/compare/${c.slug}`} className="block rounded-lg border border-gray-200 dark:border-gray-800 px-4 py-3 text-sm hover:border-blue-500">{c.label}</Link></li>
                                ))}
                            </ul>
                        </section>
                    )}
                </main>
                <FooterSite />
            </div>
        </>
    );
}

export const getStaticPaths: GetStaticPaths = () => ({
    paths: getComparePairs().map(([a, b]) => ({ params: { pair: compareSlug(a, b) } })),
    fallback: false,
});

export const getStaticProps: GetStaticProps<Props> = ({ params }) => {
    const ids = parseCompareSlug(String(params?.pair));
    const a = ids && getModelPage(ids[0]);
    const b = ids && getModelPage(ids[1]);
    if (!a || !b) return { notFound: true };
    const related = getComparePairs()
        .filter(([x, y]) => (x === a.id || y === a.id || x === b.id || y === b.id) && !(x === a.id && y === b.id))
        .slice(0, 6)
        .map(([x, y]) => ({ slug: compareSlug(x, y), label: `${getModelPage(x)!.name} vs ${getModelPage(y)!.name}` }));
    return { props: { a, b, findings: compareFindings(a, b), related } };
};
